// Onglet « Matières ».
//
// Contrairement aux machines, ce référentiel se remplit tout seul à
// partir des plans lus par l'agent 1. Les lignes qu'il crée portent une
// pastille de provenance et attendent une densité et un prix : ce sont
// des données de Mertens, jamais devinées.

import { useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Table from '../../ui/Table.jsx';
import Button from '../../ui/Button.jsx';
import Badge from '../../ui/Badge.jsx';
import Modal from '../../ui/Modal.jsx';
import { TextField } from '../../ui/TextField.jsx';
import NumberField from '../../ui/NumberField.jsx';
import SourceDot from '../../ui/SourceDot.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useCrudList } from './useCrudList.js';
import { formatEuro, formatNumber } from '../../lib/format.js';

const EMPTY = { designation: '', norm: '', density: null, price_per_kg: null, category: '', aliases: [], usinabilite: null, price_confirmed: false };

export function MatieresPanel({ isAdmin }) {
  const list = useCrudList({ path: '/catalogue/materials?all=true', key: 'materials', label: 'Matière' });

  const incomplete = list.rows.filter((m) => !m.density || !m.price_per_kg);

  const columns = [
    {
      key: 'designation',
      label: 'Désignation',
      render: (m) => (
        <span className="row">
          <SourceDot source={m.confirmed ? 'manual' : 'ai'} title="Lue sur un plan, à confirmer" />
          {m.designation}
        </span>
      ),
    },
    { key: 'norm', label: 'Norme', render: (m) => m.norm || '—' },
    {
      key: 'density',
      label: 'Densité',
      numeric: true,
      render: (m) => (m.density ? formatNumber(m.density) : <Badge tone="danger">absente</Badge>),
    },
    {
      key: 'price_per_kg',
      label: 'Prix au kilo',
      numeric: true,
      render: (m) =>
        m.price_per_kg ? formatEuro(m.price_per_kg) : <Badge tone="danger">absent</Badge>,
    },
    { key: 'aliases', label: 'Alias', render: (m) => (m.aliases ?? []).join(', ') || '—' },
    { key: 'usinabilite', label: 'Usinabilité', numeric: true, render: (m) => m.usinabilite ?? '—' },
    {
      key: 'price_confirmed',
      label: '',
      render: (m) =>
        m.price_per_kg != null && !m.price_confirmed ? (
          <Badge tone="warn">prix indicatif</Badge>
        ) : null,
    },
    ...(isAdmin
      ? [
          {
            key: 'actions',
            label: '',
            render: (m) => (
              <Button size="sm" variant="ghost" onClick={() => list.setEditing(m)}>
                Modifier
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Card>
        <CardHead title="Matières">
          {isAdmin ? (
            <Button size="sm" variant="primary" onClick={() => list.setEditing(EMPTY)}>
              Nouvelle matière
            </Button>
          ) : null}
        </CardHead>
        <CardBody className="stack">
          {incomplete.length > 0 ? (
            <div className="warnings" role="status">
              <p className="caps">{incomplete.length} matière(s) sans densité ou sans prix</p>
              <p>
                Une pièce qui les utilise sera marquée incomplète et ne pourra pas être envoyée.
                C’est voulu : mieux vaut un blocage visible qu’un prix faux.
              </p>
            </div>
          ) : null}

          {list.loading ? (
            <SkeletonRows rows={4} />
          ) : list.error ? (
            <ErrorState error={list.error} onRetry={list.reload} />
          ) : (
            <Table
              columns={columns}
              rows={list.rows}
              caption="Référentiel matières"
              empty={
                <EmptyState title="Aucune matière">
                  Ce référentiel se remplit tout seul à partir des plans. Vous pouvez aussi ajouter
                  les matières courantes de l’atelier dès maintenant.
                </EmptyState>
              }
            />
          )}
        </CardBody>
      </Card>

      {list.editing ? (
        <MatiereForm
          material={list.editing}
          errors={list.errors}
          pending={list.pending}
          onClose={() => list.setEditing(null)}
          onSave={list.save}
        />
      ) : null}
    </>
  );
}

function MatiereForm({ material, errors, pending, onClose, onSave }) {
  const [form, setForm] = useState({ ...material, aliases: material.aliases ?? [] });
  const [aliasText, setAliasText] = useState((material.aliases ?? []).join(', '));
  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <Modal
      open
      onClose={onClose}
      title={material.id ? form.designation : 'Nouvelle matière'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" form="form-matiere" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      <form
        id="form-matiere"
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          onSave(
            {
              ...form,
              aliases: aliasText.split(',').map((a) => a.trim()).filter(Boolean),
            },
            material.id,
          );
        }}
      >
        {material.id && !material.confirmed ? (
          <p className="field__hint">
            Cette matière a été créée à partir d’un plan. L’enregistrer la confirme.
          </p>
        ) : null}
        <TextField label="Désignation" required value={form.designation ?? ''}
          error={errors.designation}
          onChange={(e) => set({ designation: e.target.value })} />
        <div className="grid-2">
          <TextField label="Norme" value={form.norm ?? ''} error={errors.norm}
            onChange={(e) => set({ norm: e.target.value })} />
          <TextField label="Catégorie" value={form.category ?? ''} error={errors.category}
            hint="Acier, inox, aluminium, polymère…"
            onChange={(e) => set({ category: e.target.value })} />
          <NumberField label="Densité" unit="g/cm³" value={form.density} min={0}
            error={errors.density}
            onChange={(v) => set({ density: v })} />
          <NumberField label="Usinabilité" value={form.usinabilite} min={1} max={1000}
            error={errors.usinabilite}
            hint="Base 100 = acier de décolletage. Sert au rapprochement de pièces, pas au prix."
            onChange={(v) => set({ usinabilite: v })} />
          <NumberField label="Prix au kilo" unit="€/kg" value={form.price_per_kg} min={0}
            error={errors.price_per_kg}
            hint={
              form.price_per_kg != null && !form.price_confirmed
                ? 'Prix INDICATIF du marché, pas celui de votre fournisseur.'
                : undefined
            }
            onChange={(v) => set({ price_per_kg: v, price_confirmed: false })} />
        </div>
        {form.price_per_kg != null && !form.price_confirmed ? (
          <label className="row">
            <input
              type="checkbox"
              aria-label="Ce prix est celui de mon fournisseur"
              checked={Boolean(form.price_confirmed)}
              onChange={(e) => set({ price_confirmed: e.target.checked })}
            />
            <span>
              Ce prix est celui de mon fournisseur. Tant que cette case n’est pas cochée, chaque
              pièce chiffrée sur cette matière porte un avertissement.
            </span>
          </label>
        ) : null}
        <TextField label="Alias" value={aliasText}
          hint="Séparés par des virgules. Servent à reconnaître la matière sur un plan."
          onChange={(e) => setAliasText(e.target.value)} />
      </form>
    </Modal>
  );
}

export default MatieresPanel;
